import React, { useState } from "react";
import { UserCheck, Plus, DollarSign, Calendar, Shield } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { Modal } from "../../components/common/Modal";
import { useNotification } from "../../app/context/NotificationContext";

export function EmployeeModal({ isOpen, onClose }) {
  const { addEmployee } = useApp();
  const { addToast } = useNotification();

  const [formData, setFormData] = useState({
    name: "",
    role: "Warehouse Staff",
    phone: "+91 98200 ",
    email: "",
    monthlySalary: 25000,
    assignedLocation: "Bhiwandi Central Mega Hub (WH-1)",
    advanceTaken: 0,
    joinDate: new Date().toISOString().slice(0, 10)
  });

  const roles = [
    "Owner / Admin",
    "Warehouse Manager",
    "Warehouse Staff",
    "Retail Shop Staff",
    "Sales Team",
    "Accountant / Finance"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addEmployee(formData);
      addToast(`Employee "${formData.name}" added to staff registry!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to register employee.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Employee / Staff Member"
      subtitle="Register payroll, location assignment, and staff credentials"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Employee Full Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Ramesh Patil"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Role / Designation *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Monthly Salary (₹) *
            </label>
            <input
              type="number"
              min="0"
              required
              value={formData.monthlySalary}
              onChange={(e) => setFormData({ ...formData, monthlySalary: Number(e.target.value) })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono font-bold"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Phone Number *
            </label>
            <input
              type="text"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Assigned Warehouse / Shop
            </label>
            <input
              type="text"
              value={formData.assignedLocation}
              onChange={(e) => setFormData({ ...formData, assignedLocation: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Register Staff Member
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function EmployeesModule() {
  const { employees } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = [
    {
      header: "Employee Name",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400 font-mono">ID: {row.id} • Joined: {formatDate(row.joinDate)}</p>
        </div>
      )
    },
    {
      header: "Enterprise Role",
      accessorKey: "role",
      render: (val) => <Badge variant="blue">{val}</Badge>
    },
    {
      header: "Location Assigned",
      accessorKey: "assignedLocation",
      render: (val) => <span className="text-slate-700 dark:text-slate-300 text-xs">{val?.split("(")[0]}</span>
    },
    {
      header: "Monthly Salary (₹)",
      accessorKey: "monthlySalary",
      align: "right",
      render: (val) => (
        <span className="font-mono font-bold text-slate-900 dark:text-white">
          {formatCurrency(val)}/mo
        </span>
      )
    },
    {
      header: "Advance Balance",
      accessorKey: "advanceTaken",
      align: "right",
      render: (val) => (
        <span
          className={`font-mono ${
            val > 0 ? "text-amber-600 dark:text-amber-400 font-bold" : "text-slate-400"
          }`}
        >
          {val > 0 ? formatCurrency(val) : "None"}
        </span>
      )
    },
    {
      header: "Status",
      accessorKey: "status",
      render: () => <Badge variant="emerald">Active</Badge>
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-500" />
            Employees, Payroll & Salary Advances
          </h2>
          <p className="text-xs text-slate-500">
            Staff directory, monthly compensation records, location assignments, and salary advance tracking
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Add Staff Member
        </button>
      </div>

      <DataTable
        columns={columns}
        data={employees}
        title="Employee Directory & Compensation Ledger"
        subtitle="Manage warehouse personnel, cashiers, accountants, and field sales teams"
        filename="kirana_employees"
      />

      <EmployeeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
